import React, { useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Store, Users, ClipboardList, CheckCircle2, XCircle, Truck, Package, RefreshCw } from "lucide-react";

const inr = (n) => `₹${Number(n || 0).toLocaleString("en-IN", { maximumFractionDigits: 2 })}`;

export default function ShopAdminPage() {
  return (
    <div className="p-6 space-y-6" data-testid="shop-admin-page">
      <div>
        <div className="uc-label">B2B / Wholesale ops</div>
        <h1 className="font-[Outfit] text-3xl sm:text-4xl font-semibold text-[#2C1810] mt-1 flex items-center gap-2">
          <Store className="w-7 h-7 text-[#C05621]" /> Shop Operations
        </h1>
        <p className="text-sm text-[#8B7355] mt-1">Approve wholesale accounts and drive orders from placement to delivery.</p>
      </div>
      <Tabs defaultValue="accounts">
        <TabsList>
          <TabsTrigger value="accounts" data-testid="shop-admin-tab-accounts"><Users className="w-4 h-4 mr-1" /> Accounts</TabsTrigger>
          <TabsTrigger value="orders" data-testid="shop-admin-tab-orders"><ClipboardList className="w-4 h-4 mr-1" /> Orders</TabsTrigger>
        </TabsList>
        <TabsContent value="accounts" className="pt-4"><AccountsPanel /></TabsContent>
        <TabsContent value="orders" className="pt-4"><OrdersPanel /></TabsContent>
      </Tabs>
    </div>
  );
}

function AccountsPanel() {
  const [status, setStatus] = useState("pending");
  const [rows, setRows] = useState([]);
  const [q, setQ] = useState("");
  const [detail, setDetail] = useState(null);

  const load = async () => {
    try {
      const res = await api.get(`/shop/admin/accounts${status ? `?status=${status}` : ""}`);
      setRows(res.data);
    } catch (e) { toast.error("Failed to load"); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [status]);

  const filtered = useMemo(() => {
    if (!q) return rows;
    const t = q.toLowerCase();
    return rows.filter((r) => (r.business_name + r.email + r.phone).toLowerCase().includes(t));
  }, [rows, q]);

  const act = async (aid, action) => {
    try {
      await api.post(`/shop/admin/accounts/${aid}/${action}`);
      toast.success(`Account ${action}d`);
      await load();
      setDetail(null);
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  return (
    <div className="space-y-4">
      <div className="flex gap-2 items-end">
        {["pending", "approved", "rejected", "suspended", ""].map((s) => (
          <button
            key={s || "all"}
            onClick={() => setStatus(s)}
            className={`px-3 py-1.5 text-xs rounded-full border ${status === s ? "bg-[#C05621] text-white border-[#C05621]" : "bg-white border-[#EAE5D9]"}`}
            data-testid={`shop-accounts-tab-${s || "all"}`}
          >{s || "all"}</button>
        ))}
        <Input placeholder="Search…" value={q} onChange={(e) => setQ(e.target.value)} className="ml-auto max-w-xs" data-testid="shop-accounts-search" />
        <Button variant="outline" onClick={load}><RefreshCw className="w-3 h-3 mr-1" /> Refresh</Button>
      </div>
      <div className="rounded-lg border border-[#EAE5D9] overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-[#FAF6EE] text-[#8B7355] text-xs uppercase">
            <tr>
              <th className="text-left px-4 py-2">Business</th>
              <th className="text-left px-4 py-2">Contact</th>
              <th className="text-left px-4 py-2">Phone</th>
              <th className="text-left px-4 py-2">City</th>
              <th className="text-left px-4 py-2">Status</th>
              <th className="text-left px-4 py-2">Created</th>
              <th className="px-4 py-2"></th>
            </tr>
          </thead>
          <tbody data-testid="shop-accounts-body">
            {filtered.map((a) => (
              <tr key={a.id} className="border-t border-[#EAE5D9] hover:bg-[#FAF6EE]" data-testid={`shop-account-${a.id}`}>
                <td className="px-4 py-2">
                  <div className="font-medium">{a.business_name}</div>
                  <div className="text-xs text-[#8B7355]">{a.email}</div>
                </td>
                <td className="px-4 py-2">{a.contact_name}</td>
                <td className="px-4 py-2">{a.phone}</td>
                <td className="px-4 py-2">{a.city || "—"}</td>
                <td className="px-4 py-2"><Badge className="border-0">{a.status}</Badge></td>
                <td className="px-4 py-2 text-xs text-[#8B7355]">{new Date(a.created_at).toLocaleString("en-IN")}</td>
                <td className="px-4 py-2 text-right">
                  <Button size="sm" variant="outline" onClick={() => setDetail(a)} data-testid={`shop-account-open-${a.id}`}>Review</Button>
                </td>
              </tr>
            ))}
            {filtered.length === 0 && (
              <tr><td colSpan={7} className="px-4 py-8 text-center text-[#8B7355]">No accounts</td></tr>
            )}
          </tbody>
        </table>
      </div>

      <Dialog open={!!detail} onOpenChange={(o) => !o && setDetail(null)}>
        <DialogContent className="max-w-lg bg-white">
          <DialogHeader><DialogTitle className="font-[Outfit]">{detail?.business_name}</DialogTitle></DialogHeader>
          {detail && (
            <div className="space-y-2 text-sm">
              <Row label="Contact" value={detail.contact_name} />
              <Row label="Email" value={detail.email} />
              <Row label="Phone" value={detail.phone} />
              <Row label="GSTIN" value={detail.gstin || "—"} />
              <Row label="Address" value={detail.address || "—"} />
              <Row label="City" value={detail.city || "—"} />
              <Row label="Notes" value={detail.notes || "—"} />
              <Row label="Status" value={<Badge className="border-0">{detail.status}</Badge>} />
            </div>
          )}
          <DialogFooter className="flex gap-2">
            {detail?.status === "pending" && (
              <>
                <Button onClick={() => act(detail.id, "reject")} variant="outline" className="border-red-500 text-red-700" data-testid="shop-account-reject">
                  <XCircle className="w-3 h-3 mr-1" /> Reject
                </Button>
                <Button onClick={() => act(detail.id, "approve")} className="bg-emerald-600 hover:bg-emerald-700 text-white" data-testid="shop-account-approve">
                  <CheckCircle2 className="w-3 h-3 mr-1" /> Approve
                </Button>
              </>
            )}
            {detail?.status === "approved" && (
              <Button onClick={() => act(detail.id, "suspend")} variant="outline" className="border-red-500 text-red-700" data-testid="shop-account-suspend">Suspend</Button>
            )}
            <Button variant="outline" onClick={() => setDetail(null)}>Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function OrdersPanel() {
  const [status, setStatus] = useState("pending");
  const [rows, setRows] = useState([]);
  const [detail, setDetail] = useState(null);

  const load = async () => {
    try {
      const res = await api.get(`/shop/admin/orders${status ? `?status=${status}` : ""}`);
      setRows(res.data);
    } catch (e) { toast.error("Failed to load"); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [status]);

  const act = async (oid, action) => {
    try {
      const res = await api.post(`/shop/admin/orders/${oid}/${action}`);
      toast.success(`Order ${action}${action.endsWith("e") ? "d" : "ed"}`);
      setDetail(res.data);
      await load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  return (
    <div className="space-y-4">
      <div className="flex gap-2 items-end">
        {["pending", "confirmed", "ready", "dispatched", "delivered", "cancelled", ""].map((s) => (
          <button
            key={s || "all"}
            onClick={() => setStatus(s)}
            className={`px-3 py-1.5 text-xs rounded-full border ${status === s ? "bg-[#C05621] text-white border-[#C05621]" : "bg-white border-[#EAE5D9]"}`}
            data-testid={`shop-orders-tab-${s || "all"}`}
          >{s || "all"}</button>
        ))}
        <Button variant="outline" onClick={load} className="ml-auto"><RefreshCw className="w-3 h-3 mr-1" /> Refresh</Button>
      </div>
      <div className="rounded-lg border border-[#EAE5D9] overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-[#FAF6EE] text-[#8B7355] text-xs uppercase">
            <tr>
              <th className="text-left px-4 py-2">Order</th>
              <th className="text-left px-4 py-2">Business</th>
              <th className="text-left px-4 py-2">Fulfilment</th>
              <th className="text-left px-4 py-2">Status</th>
              <th className="text-right px-4 py-2">Total</th>
              <th className="text-left px-4 py-2">Placed</th>
              <th className="px-4 py-2"></th>
            </tr>
          </thead>
          <tbody data-testid="shop-orders-body">
            {rows.map((o) => (
              <tr key={o.id} className="border-t border-[#EAE5D9] hover:bg-[#FAF6EE]" data-testid={`shop-order-${o.id}`}>
                <td className="px-4 py-2 font-mono text-xs">{o.number}</td>
                <td className="px-4 py-2">{o.b2b_business_name}</td>
                <td className="px-4 py-2 text-xs text-[#8B7355]">
                  {o.fulfilment_type === "pickup" ? `Pickup · ${o.pickup_branch_name || "-"}` : `Delivery · ${o.delivery_date || "-"}`}
                </td>
                <td className="px-4 py-2"><Badge className="border-0">{o.status}</Badge></td>
                <td className="px-4 py-2 text-right font-semibold">{inr(o.grand_total)}</td>
                <td className="px-4 py-2 text-xs text-[#8B7355]">{new Date(o.created_at).toLocaleString("en-IN")}</td>
                <td className="px-4 py-2 text-right"><Button size="sm" variant="outline" onClick={() => setDetail(o)} data-testid={`shop-order-open-${o.id}`}>Open</Button></td>
              </tr>
            ))}
            {rows.length === 0 && <tr><td colSpan={7} className="px-4 py-8 text-center text-[#8B7355]">No orders</td></tr>}
          </tbody>
        </table>
      </div>

      <Dialog open={!!detail} onOpenChange={(o) => !o && setDetail(null)}>
        <DialogContent className="max-w-3xl bg-white max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle className="font-[Outfit]">Order {detail?.number}</DialogTitle></DialogHeader>
          {detail && (
            <div className="space-y-4 text-sm">
              <div className="grid grid-cols-2 gap-3">
                <Row label="Business" value={detail.b2b_business_name} />
                <Row label="Contact" value={`${detail.b2b_email} · ${detail.b2b_phone}`} />
                <Row label="Fulfilment" value={detail.fulfilment_type === "pickup" ? `Pickup · ${detail.pickup_branch_name}` : `Delivery · ${detail.delivery_date}`} />
                {detail.delivery_address && <Row label="Address" value={`${detail.delivery_address}, ${detail.delivery_city || ""} ${detail.delivery_pincode || ""}`} />}
                <Row label="Status" value={<Badge className="border-0">{detail.status}</Badge>} />
                {detail.sale_number && <Row label="Invoice" value={detail.sale_number} />}
              </div>
              <div className="rounded border border-[#EAE5D9] overflow-hidden">
                <table className="w-full text-sm">
                  <thead className="bg-[#FAF6EE] text-[#8B7355] text-[10px] uppercase">
                    <tr><th className="text-left px-3 py-2">Product</th><th className="text-right px-3 py-2">Qty</th><th className="text-right px-3 py-2">Unit</th><th className="text-right px-3 py-2">Total</th></tr>
                  </thead>
                  <tbody>
                    {detail.items.map((it, i) => (
                      <tr key={i} className="border-t border-[#EAE5D9]">
                        <td className="px-3 py-2">{it.name}<div className="text-[10px] text-[#8B7355]">{it.sku}{it.bulk_discount_pct > 0 && ` · ${it.bulk_discount_pct}% bulk`}</div></td>
                        <td className="px-3 py-2 text-right">{it.quantity}</td>
                        <td className="px-3 py-2 text-right">{inr(it.unit_price)}</td>
                        <td className="px-3 py-2 text-right">{inr(it.line_total)}</td>
                      </tr>
                    ))}
                    <tr className="font-semibold border-t-2 border-[#2C1810]"><td colSpan={3} className="px-3 py-2 text-right">Total (credit)</td><td className="px-3 py-2 text-right text-[#C05621]">{inr(detail.grand_total)}</td></tr>
                  </tbody>
                </table>
              </div>
              {detail.notes && <div className="text-xs text-[#8B7355] italic">"{detail.notes}"</div>}
            </div>
          )}
          <DialogFooter className="flex gap-2 flex-wrap">
            {detail?.status === "pending" && (
              <Button onClick={() => act(detail.id, "confirm")} className="bg-emerald-600 hover:bg-emerald-700 text-white" data-testid="shop-order-confirm">
                <CheckCircle2 className="w-3 h-3 mr-1" /> Confirm (post invoice)
              </Button>
            )}
            {detail?.status === "confirmed" && <Button onClick={() => act(detail.id, "ready")} data-testid="shop-order-ready"><Package className="w-3 h-3 mr-1" /> Mark ready</Button>}
            {detail?.status === "ready" && detail?.fulfilment_type === "delivery" && (
              <Button onClick={() => act(detail.id, "dispatch")} data-testid="shop-order-dispatch"><Truck className="w-3 h-3 mr-1" /> Dispatch</Button>
            )}
            {(detail?.status === "ready" || detail?.status === "dispatched" || detail?.status === "confirmed") && (
              <Button onClick={() => act(detail.id, "deliver")} className="bg-[#2C1810] text-white" data-testid="shop-order-deliver">Mark delivered</Button>
            )}
            {!["delivered", "cancelled"].includes(detail?.status) && (
              <Button onClick={() => act(detail.id, "cancel")} variant="outline" className="border-red-500 text-red-700" data-testid="shop-order-cancel"><XCircle className="w-3 h-3 mr-1" /> Cancel</Button>
            )}
            <Button variant="outline" onClick={() => setDetail(null)}>Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function Row({ label, value }) {
  return <div className="flex items-baseline justify-between"><span className="text-[#8B7355] text-xs">{label}</span><span className="text-right">{value}</span></div>;
}
